import React from "react";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram } from "lucide-react";

const FACEBOOK_DEEP = "fb://facewebmodal/fbrequesturl?url=https://www.facebook.com/profile.php?id=61578101584273";
const FACEBOOK_FALLBACK = "https://www.facebook.com/profile.php?id=61578101584273";
const INSTAGRAM_DEEP = "instagram://user?username=premium.autowork";
const INSTAGRAM_FALLBACK = "https://www.instagram.com/premium.autowork";

const handleDeepLink = (deep, fallback) => (e) => {
  e.preventDefault();
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  if (isMobile) {
    const timeout = setTimeout(() => {
      window.location.href = fallback;
    }, 800);
    window.location.href = deep;
    window.addEventListener("blur", () => clearTimeout(timeout), { once: true });
  } else {
    window.open(fallback, "_blank", "noopener,noreferrer");
  }
};

export default function Footer() {
  return (
    <footer
      data-testid="footer"
      className="relative bg-[#0a0a0a] border-t border-white/5 pt-20 pb-10"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#F5C518]/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-14">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_premium-shop-book/artifacts/qj6if298_IMG_7610.PNG"
                alt="Premium Auto Works"
                className="h-24 w-auto object-contain"
              />
            </div>
            <p className="text-sm text-neutral-400 leading-relaxed max-w-sm">
              Ottawa&apos;s elite, full-service mechanic shop. Dealership-grade precision, independent-shop value.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <a
                href={FACEBOOK_FALLBACK}
                onClick={handleDeepLink(FACEBOOK_DEEP, FACEBOOK_FALLBACK)}
                data-testid="social-facebook"
                aria-label="Facebook"
                className="w-10 h-10 rounded-full border border-white/10 hover:border-[#F5C518] hover:bg-[#F5C518]/10 flex items-center justify-center transition-all group"
              >
                <Facebook className="w-4 h-4 text-neutral-400 group-hover:text-[#F5C518]" />
              </a>
              <a
                href={INSTAGRAM_FALLBACK}
                onClick={handleDeepLink(INSTAGRAM_DEEP, INSTAGRAM_FALLBACK)}
                data-testid="social-instagram"
                aria-label="Instagram"
                className="w-10 h-10 rounded-full border border-white/10 hover:border-[#F5C518] hover:bg-[#F5C518]/10 flex items-center justify-center transition-all group"
              >
                <Instagram className="w-4 h-4 text-neutral-400 group-hover:text-[#F5C518]" />
              </a>
            </div>
          </div>

          {/* Hours */}
          <div className="md:col-span-3">
            <h4 className="font-display font-bold text-xs uppercase tracking-[0.2em] text-[#F5C518] mb-4">
              Hours
            </h4>
            <div className="space-y-2 text-sm text-neutral-300">
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-[#F5C518] mt-0.5 shrink-0" />
                <div>
                  <div>Mon – Fri: 9:00 AM – 6:00 PM</div>
                  <div className="text-neutral-500">Sat & Sun: Closed</div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <h4 className="font-display font-bold text-xs uppercase tracking-[0.2em] text-[#F5C518] mb-4">
              Contact
            </h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href="tel:6138222333"
                  data-testid="footer-phone"
                  className="flex items-center gap-2.5 text-neutral-300 hover:text-[#F5C518] transition-colors"
                >
                  <Phone className="w-4 h-4 shrink-0" />
                  (613) 822-2333
                </a>
              </li>
              <li>
                <a
                  href="mailto:premium_autoworks@outlook.com"
                  data-testid="footer-email"
                  className="flex items-center gap-2.5 text-neutral-300 hover:text-[#F5C518] transition-colors break-all"
                >
                  <Mail className="w-4 h-4 shrink-0" />
                  premium_autoworks@outlook.com
                </a>
              </li>
              <li>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=2588+Del+Zotto+Ave+Gloucester+ON+K1T+3V7"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-address"
                  className="flex items-start gap-2.5 text-neutral-300 hover:text-[#F5C518] transition-colors"
                >
                  <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    2588 Del Zotto Ave<br />
                    Gloucester, ON K1T 3V7
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-neutral-500">
          <div>© {new Date().getFullYear()} Premium Auto Works. All rights reserved.</div>
          <div className="uppercase tracking-wider">Built for drivers who demand precision.</div>
        </div>
      </div>
    </footer>
  );
}
